import { isPlatformBrowser } from '@angular/common';
import { Component, inject, Inject, PLATFORM_ID, signal } from '@angular/core';
import { AuthService } from './shared/services/auth.service';
import { LocalStorageService, STORAGE_CONSTANTS } from './shared/services/storage.service';
import { lastValueFrom } from 'rxjs';
import { Logger } from './shared/utils/log-util';
import { AppService } from './shared/services/app.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { VerifyPhoneComponent } from './pages/auth/verify-phone/verify-phone.component';
import { ROLES } from './app.const';

@Component({
    selector: 'qatch-app',
    templateUrl: './qatch-app.component.html'
})
export class QatchAppComponent {
    authService = inject(AuthService);
    signInProgress: boolean;
    currentRoute: string;

    constructor(
        private storageService: LocalStorageService,
        private appService: AppService,
        private ngbModal: NgbModal,
        @Inject(PLATFORM_ID) private platformId: object
    ) {
      this.authService.isReady.set(false);
      if (!isPlatformBrowser(this.platformId)) {
        this.authService.isReady.set(true);
        this.authService.authReady$$.next(true);
        return;
      }
      this.getAuthState();
      const urlParams = new URLSearchParams(window.location.search);
      this.currentRoute = this.authService.getCurrentRouteWithoutMagictoken(urlParams);
      const magiclinkToken = urlParams.get('magic-token') || urlParams.get('magic_token') || urlParams.get('MagicToken');
      if(magiclinkToken && !this.signInProgress) {
          this.loginByMagiclinkToken(magiclinkToken);
      }
    }

    async getAuthState() {
        // Add listener for listening to Firebase auth state events
      await this.authService.writeAuthToken();
      this.authService.authState().subscribe(async (result) => {
        await this.authService.writeAuthToken();
        if (result) {
            // Set auto mode on for refreshing Auth token
            this.authService.setAutoRefreshAuthToken();
            this.authService.me(true).then((user) => {
              if(user?.roles?.includes(ROLES.SUBSCRIBER) && location.pathname === '/') {
                this.appService.navigateWithMerge('/shop');
              }
            }).catch((error) => {
              Logger.debugError(error);
            }).finally(()=>{
              this.markAuthReady(false);
            });
        } else {
            this.markAuthReady(false);
        }
      });
    }

    async loginByMagiclinkToken(magicToken: string) {
      this.signInProgress = true;
      this.storageService.set(STORAGE_CONSTANTS.REDIRECT_TO_URL_ON_SIGNIN, this.currentRoute);
      lastValueFrom(this.authService.createCustomToken({magicToken}))
      .then(async (resp: any) => {
        const rTokenId = resp?.request_token_id;
        if(rTokenId) {
          this.storageService.storeInSession(STORAGE_CONSTANTS.REQUEST_TOKEN_ID, rTokenId);
          this.openPhoneVerificationPopup(magicToken, resp.message);
        } else {
          await this.authService.logOut();
          this.authService.signInWithCustomToken(resp).then(() => {
            this.storageService.storeInAppCache(STORAGE_CONSTANTS.JUST_LOGGED_IN_EVENT, 'true');
            this.markAuthReady(true);
          }).catch((error) => {
            this.handleMagicLinkAuthFailure(error);
          });
        }
      })
      .catch((error) => {
          this.handleMagicLinkAuthFailure(error);
      });
    }

    openPhoneVerificationPopup(magicToken: string, message: string) {
      const modalRef = this.ngbModal.open(VerifyPhoneComponent, {
          centered: true,
          scrollable: true,
          keyboard: false,
          size: 'xl',
          backdrop: 'static',
          windowClass: 'convo-form-modal verify-account-phone-popup'
      });
      modalRef.componentInstance.magiclinkTokenLogin = true;
      modalRef.componentInstance.hintMssage = message;
      modalRef.componentInstance.onSuccess = () => {
          modalRef.close();
          this.loginByMagiclinkToken(magicToken);
      }
      modalRef.componentInstance.onError = (skipToLogin) => {
        if(skipToLogin) {
          modalRef.close();
          this.authService.isReady.set(true);
          this.appService.navigateByURL('/signin');
        }
      }
    }

    handleMagicLinkAuthFailure(error: any){
      this.markAuthReady(true);
      Logger.debugError(error);
      const redirectTo = this.storageService.pop(STORAGE_CONSTANTS.REDIRECT_TO_URL_ON_SIGNIN);
      this.appService.navigateByURL(redirectTo);
      // setTimeout(() => {
      //   if(!this.authService.isLoggedIn()){
      //     this.toastService.error(error);
      //   }
      // });
    }

    markAuthReady(resetSignProgress: boolean){
      if(resetSignProgress){
          this.signInProgress = false;
          this.authService.isReady.set(true);
      }else if(this.signInProgress){
          this.authService.isReady.set(false);
      }else{
          this.authService.isReady.set(true);
      }
        this.authService.profileUpdated.set(true);
        if (this.authService.isReady()) this.authService.authReady$$.next(true);
    }
}
